/**
 * RealtimeEventHub - Centralized Real-time Event Handler
 * 
 * Handles all real-time database events and provides:
 * - Toast notifications for critical events
 * - React Query cache invalidation
 * - Event logging and analytics
 * - Optimistic UI updates
 * 
 * Canon: SUPABANK REALTIME v1.0.0
 * Migration: E3_2025-11-04/007
 */

import { useEffect } from 'react';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  PartyPopper,
  Building2,
  Lock,
  Wallet,
  XCircle,
  Landmark,
  Search,
  FileText,
  ArrowUpRight,
  Link2,
  Star,
  Banknote,
  Crown,
  type LucideIcon,
} from 'lucide-react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';

// Helper for toast title with icon (Canon: no emojis in UI)
const ToastTitle = ({ icon: Icon, children, className }: {
  icon: LucideIcon | (() => React.ReactElement);
  children: React.ReactNode;
  className?: string;
}) => (
  <span className="inline-flex items-center gap-1.5">
    {typeof Icon === 'function' ? <Icon /> : <Icon className={`w-4 h-4 flex-shrink-0 ${className || ''}`} />}
    {children}
  </span>
);

interface RealtimeEventHubProps {
  /**
   * Filter events for specific customer (optional)
   * If provided, only receives events for this customer
   */
  customerId?: string;

  /**
   * Enable/disable all subscriptions
   * Useful for debugging or temporary pause
   */
  enabled?: boolean;

  /**
   * Enable verbose logging for debugging
   */
  debug?: boolean;
}

export function RealtimeEventHub({
  customerId,
  enabled = true,
  debug = false
}: RealtimeEventHubProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Log hub initialization
  useEffect(() => {
    if (debug) {
      console.log('[RealtimeEventHub] Initialized', { customerId, enabled });
    }
  }, [customerId, enabled, debug]);

  // ============================================================================
  // 1. CUSTOMER EVENTS (CRITICAL)
  // ============================================================================

  useRealtimeSubscription({
    table: 'customers',
    event: '*',
    filter: customerId ? `id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const customer = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] customer:created', customer);

      toast({
        title: <ToastTitle icon={() => <SFIcon icon={sfIcons.user} />}>New Customer</ToastTitle>,
        description: `${customer.email || customer.display_name} registered`,
        duration: 5000,
      });

      // Invalidate customer lists
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
    },
    
    onUpdate: (payload) => {
      const oldCustomer = payload.old as any;
      const newCustomer = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] customer:updated', { old: oldCustomer, new: newCustomer });
      
      // KYC Status Changed
      if (oldCustomer.kyc_status !== newCustomer.kyc_status) {
        if (newCustomer.kyc_status === 'approved') {
          toast({
            title: <ToastTitle icon={PartyPopper} className="text-green-500">KYC Approved!</ToastTitle>,
            description: 'Account is now fully verified',
            variant: 'success',
            duration: 8000,
          });
        } else if (newCustomer.kyc_status === 'rejected') {
          toast({
            title: <ToastTitle icon={() => <SFIcon icon={sfIcons.alertTriangle} />} className="text-yellow-500">KYC Rejected</ToastTitle>,
            description: 'Please review rejection reasons',
            variant: 'destructive',
            duration: 10000,
          });
        } else if (newCustomer.kyc_status === 'under_review') {
          toast({
            title: <ToastTitle icon={Search}>KYC Under Review</ToastTitle>,
            description: 'Verification in progress',
            duration: 5000,
          });
        }
        
        // Invalidate customer-related queries
        queryClient.invalidateQueries({ queryKey: ['customer', newCustomer.id] });
        queryClient.invalidateQueries({ queryKey: ['customer-capabilities'] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      }
      
      // Provider ID Assigned (Bridge registration complete)
      if (!oldCustomer.provider_customer_id && newCustomer.provider_customer_id) {
        toast({
          title: <ToastTitle icon={() => <SFIcon icon={sfIcons.check} />} className="text-green-500">Bridge Registration Complete</ToastTitle>,
          description: `Provider ID: ${newCustomer.provider_customer_id.substring(0, 12)}...`,
          variant: 'success',
          duration: 6000,
        });

        queryClient.invalidateQueries({ queryKey: ['customer', newCustomer.id] });
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      }
      
      // ToS Status Changed
      if (oldCustomer.tos_status !== newCustomer.tos_status && newCustomer.tos_status === 'approved') {
        toast({
          title: <ToastTitle icon={FileText}>Terms Accepted</ToastTitle>,
          description: 'Terms of Service accepted',
          duration: 5000,
        });
      }
    },
    
    onDelete: (payload) => {
      const customer = payload.old as any;
      if (debug) console.log('[RealtimeEventHub] customer:deleted', customer);
      
      toast({
        title: 'Customer Removed',
        description: customer.email || 'Customer deleted',
        duration: 5000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    }
  });

  // ============================================================================
  // 2. WALLET EVENTS (HIGH PRIORITY)
  // ============================================================================

  useRealtimeSubscription({
    table: 'custodial_wallets',
    event: '*',
    filter: customerId ? `customer_id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const wallet = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] wallet:created', wallet);
      
      toast({
        title: <ToastTitle icon={Landmark} className="text-green-500">Wallet Created</ToastTitle>,
        description: `${wallet.chain.toUpperCase()} wallet: ${wallet.address.substring(0, 10)}...`,
        variant: 'success',
        duration: 8000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['customer-wallets', customerId] });
      queryClient.invalidateQueries({ queryKey: ['admin-wallets'] });
    },
    
    onUpdate: (payload) => {
      const oldWallet = payload.old as any;
      const newWallet = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] wallet:updated', { old: oldWallet, new: newWallet });
      
      // Wallet Frozen (CRITICAL SECURITY EVENT)
      if (oldWallet.status !== newWallet.status) {
        if (newWallet.status === 'frozen') {
          toast({
            title: <ToastTitle icon={Lock} className="text-red-500">Wallet Frozen</ToastTitle>,
            description: 'Contact support immediately',
            variant: 'destructive',
            duration: 0, // Persistent until dismissed
          });
        } else if (newWallet.status === 'active' && oldWallet.status === 'frozen') {
          toast({
            title: <ToastTitle icon={() => <SFIcon icon={sfIcons.check} />} className="text-green-500">Wallet Reactivated</ToastTitle>,
            description: 'Wallet is now active',
            variant: 'success',
            duration: 6000,
          });
        } else if (newWallet.status === 'closed') {
          toast({
            title: 'Wallet Closed',
            description: 'Wallet has been closed',
            duration: 6000,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['wallets'] });
        queryClient.invalidateQueries({ queryKey: ['wallet', newWallet.id] });
      }
    }
  });

  // ============================================================================
  // 3. WALLET BALANCE EVENTS (HIGH PRIORITY)
  // ============================================================================

  useRealtimeSubscription({
    table: 'wallet_balances',
    event: 'UPDATE',
    enabled,
    
    onUpdate: (payload) => {
      const oldBalance = payload.old as any;
      const newBalance = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] balance:updated', { old: oldBalance, new: newBalance });
      
      // Balance changed
      if (oldBalance.balance !== newBalance.balance) {
        const oldBal = parseFloat(oldBalance.balance || '0');
        const newBal = parseFloat(newBalance.balance || '0');
        const diff = newBal - oldBal;
        const direction = diff > 0 ? '+' : '';
        
        toast({
          title: <ToastTitle icon={Wallet}>Balance Updated</ToastTitle>,
          description: `${newBalance.currency}: ${direction}${diff.toFixed(6)}`,
          variant: diff > 0 ? 'success' : 'default',
          duration: 6000,
        });
        
        // Invalidate balance queries
        queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
        queryClient.invalidateQueries({ queryKey: ['wallet', newBalance.wallet_id] });
        queryClient.invalidateQueries({ queryKey: ['portfolio-value'] });
      }
    }
  });

  // ============================================================================
  // 4. WALLET TRANSACTION EVENTS (HIGH PRIORITY)
  // ============================================================================

  useRealtimeSubscription({
    table: 'wallet_transactions',
    event: '*',
    enabled,
    
    onInsert: (payload) => {
      const tx = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] wallet_tx:created', tx);
      
      toast({
        title: <ToastTitle icon={ArrowUpRight}>Transfer Initiated</ToastTitle>,
        description: `${tx.amount} ${tx.currency} - ${tx.direction === 'in' ? 'Incoming' : 'Outgoing'}`,
        duration: 6000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
    
    onUpdate: (payload) => {
      const oldTx = payload.old as any;
      const newTx = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] wallet_tx:updated', { old: oldTx, new: newTx });
      
      // Status changed
      if (oldTx.status !== newTx.status) {
        if (newTx.status === 'settled') {
          toast({
            title: <ToastTitle icon={() => <SFIcon icon={sfIcons.check} />} className="text-green-500">Transfer Complete</ToastTitle>,
            description: `${newTx.amount} ${newTx.currency} settled`,
            variant: 'success',
            duration: 8000,
          });
        } else if (newTx.status === 'failed') {
          toast({
            title: <ToastTitle icon={XCircle} className="text-red-500">Transfer Failed</ToastTitle>,
            description: 'Please contact support',
            variant: 'destructive',
            duration: 10000,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
        queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
      }
    }
  });

  // ============================================================================
  // 5. KYC EVENTS (CRITICAL)
  // ============================================================================

  useRealtimeSubscription({
    table: 'kyc_links',
    event: '*',
    filter: customerId ? `customer_id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const kycLink = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] kyc:link_created', kycLink);
      
      toast({
        title: <ToastTitle icon={Link2}>KYC Link Ready</ToastTitle>,
        description: 'Click to start verification',
        duration: 6000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['kyc-links'] });
      queryClient.invalidateQueries({ queryKey: ['customer', kycLink.customer_id] });
    },
    
    onUpdate: (payload) => {
      const oldLink = payload.old as any;
      const newLink = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] kyc:updated', { old: oldLink, new: newLink });
      
      // Status changed
      if (oldLink.status !== newLink.status) {
        if (newLink.status === 'approved') {
          toast({
            title: <ToastTitle icon={PartyPopper} className="text-green-500">KYC Approved!</ToastTitle>,
            description: 'Your account is fully verified',
            variant: 'success',
            duration: 10000,
          });
        } else if (newLink.status === 'rejected') {
          toast({
            title: <ToastTitle icon={() => <SFIcon icon={sfIcons.alertTriangle} />} className="text-yellow-500">KYC Rejected</ToastTitle>,
            description: 'Please review rejection reasons',
            variant: 'destructive',
            duration: 0, // Persistent
          });
        } else if (newLink.status === 'under_review') {
          toast({
            title: <ToastTitle icon={Search}>KYC Under Review</ToastTitle>,
            description: 'Verification in progress',
            duration: 6000,
          });
        }
        
        queryClient.invalidateQueries({ queryKey: ['kyc-links'] });
        queryClient.invalidateQueries({ queryKey: ['customer', newLink.customer_id] });
        queryClient.invalidateQueries({ queryKey: ['customer-capabilities'] });
      }
    }
  });

  // ============================================================================
  // 6. PAYOUT ROUTE EVENTS (MEDIUM PRIORITY)
  // ============================================================================

  useRealtimeSubscription({
    table: 'payout_routes',
    event: '*',
    filter: customerId ? `customer_id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const route = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] payout_route:created', route);
      
      toast({
        title: <ToastTitle icon={Building2}>Payout Route Added</ToastTitle>,
        description: `${route.bank_name || 'Bank account'} - Verification pending`,
        duration: 6000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['payout-routes'] });
    },
    
    onUpdate: (payload) => {
      const oldRoute = payload.old as any;
      const newRoute = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] payout_route:updated', { old: oldRoute, new: newRoute });
      
      // Verification status changed
      if (oldRoute.status !== newRoute.status && newRoute.status === 'verified') {
        toast({
          title: <ToastTitle icon={() => <SFIcon icon={sfIcons.check} />} className="text-green-500">Bank Account Verified</ToastTitle>,
          description: `${newRoute.bank_name} ready for payouts`,
          variant: 'success',
          duration: 8000,
        });

        queryClient.invalidateQueries({ queryKey: ['payout-routes'] });
        queryClient.invalidateQueries({ queryKey: ['external-accounts'] });
      }
      
      // Set as default
      if (!oldRoute.is_default && newRoute.is_default) {
        toast({
          title: <ToastTitle icon={Star} className="text-yellow-500">Default Payout Route</ToastTitle>,
          description: `${newRoute.bank_name} set as default`,
          duration: 5000,
        });
        
        queryClient.invalidateQueries({ queryKey: ['payout-routes'] });
      }
    }
  });

  // ============================================================================
  // 7. TRANSACTION EVENTS (MEDIUM PRIORITY)
  // ============================================================================

  useRealtimeSubscription({
    table: 'tx',
    event: '*',
    filter: customerId ? `customer_id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const tx = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] transaction:created', tx);
      
      // Only show toast for high-value transactions
      const amount = parseFloat(tx.amount_out || tx.amount_in || '0');
      if (amount > 100) {
        toast({
          title: <ToastTitle icon={Banknote}>New Transaction</ToastTitle>,
          description: `${tx.kind} - ${amount} ${tx.asset_out || tx.asset_in}`,
          duration: 6000,
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['customer-transactions'] });
    },
    
    onUpdate: (payload) => {
      const oldTx = payload.old as any;
      const newTx = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] transaction:updated', { old: oldTx, new: newTx });
      
      // Status changed
      if (oldTx.status !== newTx.status) {
        if (newTx.status === 'settled') {
          toast({
            title: <ToastTitle icon={() => <SFIcon icon={sfIcons.check} />} className="text-green-500">Transaction Settled</ToastTitle>,
            description: `${newTx.kind} complete`,
            variant: 'success',
            duration: 8000,
          });
        } else if (newTx.status === 'failed') {
          toast({
            title: <ToastTitle icon={XCircle} className="text-red-500">Transaction Failed</ToastTitle>,
            description: 'Please check transaction details',
            variant: 'destructive',
            duration: 10000,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['tx', newTx.id] });
      }
    }
  });

  // ============================================================================
  // 8. ADMIN ROLE EVENTS (CRITICAL)
  // ============================================================================

  useRealtimeSubscription({
    table: 'user_roles',
    event: '*',
    enabled,
    
    onInsert: (payload) => {
      const role = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] role:granted', role);
      
      toast({
        title: <ToastTitle icon={Crown} className="text-yellow-500">Role Granted</ToastTitle>,
        description: `You now have ${role.role.toUpperCase()} access`,
        variant: 'success',
        duration: 10000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['user-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-access'] });
      
      // Trigger page reload to update permissions
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    },
    
    onDelete: (payload) => {
      const role = payload.old as any;
      if (debug) console.log('[RealtimeEventHub] role:revoked', role);

      toast({
        title: <ToastTitle icon={() => <SFIcon icon={sfIcons.alertTriangle} />} className="text-yellow-500">Role Revoked</ToastTitle>,
        description: `${role.role.toUpperCase()} access removed`,
        variant: 'destructive',
        duration: 0, // Persistent
      });

      queryClient.invalidateQueries({ queryKey: ['user-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-access'] });

      // Redirect if in admin area
      if (window.location.pathname.startsWith('/admin')) {
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    }
  });

  // ============================================================================
  // 9. SECURITY EVENTS (CRITICAL)
  // ============================================================================

  useRealtimeSubscription({
    table: 'security_events',
    event: 'INSERT',
    filter: customerId ? `customer_id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const event = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] security:event', event);
      
      // Show toast only for warning and error severity
      if (event.severity === 'error' || event.severity === 'warning') {
        toast({
          title: event.severity === 'error'
            ? <ToastTitle icon={() => <SFIcon icon={sfIcons.security} />} className="text-red-500">Security Alert</ToastTitle>
            : <ToastTitle icon={() => <SFIcon icon={sfIcons.alertTriangle} />} className="text-yellow-500">Security Warning</ToastTitle>,
          description: event.event_type.replace(/_/g, ' ').toUpperCase(),
          variant: 'destructive',
          duration: 15000,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['security-events'] });
    }
  });

  // ============================================================================
  // 10. VIRTUAL ACCOUNT EVENTS (MEDIUM PRIORITY)
  // ============================================================================

  useRealtimeSubscription({
    table: 'virtual_accounts',
    event: '*',
    filter: customerId ? `customer_id=eq.${customerId}` : undefined,
    enabled,
    
    onInsert: (payload) => {
      const va = payload.new as any;
      if (debug) console.log('[RealtimeEventHub] va:created', va);
      
      toast({
        title: <ToastTitle icon={Landmark} className="text-green-500">Virtual Account Ready</ToastTitle>,
        description: `${va.source_currency.toUpperCase()} → ${va.destination_currency.toUpperCase()}`,
        variant: 'success',
        duration: 8000,
      });
      
      queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
    },
    
    onUpdate: (payload) => {
      const oldVA = payload.old as any;
      const newVA = payload.new as any;
      
      if (debug) console.log('[RealtimeEventHub] va:updated', { old: oldVA, new: newVA });
      
      // Status changed
      if (oldVA.status !== newVA.status) {
        if (newVA.status === 'activated') {
          toast({
            title: <ToastTitle icon={() => <SFIcon icon={sfIcons.check} />} className="text-green-500">Virtual Account Activated</ToastTitle>,
            description: 'Ready to receive deposits',
            variant: 'success',
            duration: 6000,
          });
        } else if (newVA.status === 'deactivated') {
          toast({
            title: 'Virtual Account Deactivated',
            description: 'No longer accepting deposits',
            duration: 6000,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
        queryClient.invalidateQueries({ queryKey: ['va', newVA.id] });
      }
    }
  });

  // Event hub is invisible (no UI)
  return null;
}

/**
 * Export for use in App.tsx or Shell components
 * 
 * Usage:
 * ```tsx
 * <RealtimeEventHub enabled={true} debug={isDev} />
 * ```
 */
export default RealtimeEventHub;

